import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `R ${(parseFloat(v) || 0).toFixed(2)}`;

export const FixLineProfitDialog = ({ line, onClose, onSaved }) => {
  const [profit, setProfit] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (line) { setProfit(""); setPassword(""); }
  }, [line]);

  if (!line) return null;
  const newProfit = parseFloat(profit);
  const newCost = !isNaN(newProfit) && line.qty ? (line.line_excl - newProfit) / line.qty : null;

  const save = async (e) => {
    e.preventDefault();
    if (isNaN(newProfit)) return toast.error("Enter the correct profit");
    if (newCost < 0) return toast.error("Profit can't be more than the line's selling value");
    if (!password) return toast.error("Enter your password");
    setSaving(true);
    try {
      await axios.post(`${API}/sales/${line.sale_id}/fix-line-profit`, {
        line_index: line.line_index, new_profit: newProfit, line_excl: line.line_excl, password,
      });
      toast.success(`Profit on ${line.inv_no} updated to ${fmt(newProfit)}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!line} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="fix-profit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4" />Fix Profit: {line.inv_no}</DialogTitle>
        </DialogHeader>
        <div className="rounded-md bg-gray-50 p-3 text-sm space-y-1">
          <div className="font-medium">{line.description} × {line.qty}</div>
          <div>Selling excl VAT: <strong>{fmt(line.line_excl)}</strong> · Current profit: <strong className={line.profit < 0 ? "text-red-600" : "text-green-600"}>{fmt(line.profit)}</strong></div>
          <div className="text-xs text-gray-500">Only the cost recorded on this line changes. Invoice, totals and customer account stay the same, and nothing is emailed.</div>
        </div>
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label className="text-sm">Correct profit for this line (R)</Label>
            <Input type="number" step="0.01" value={profit} onChange={(e) => setProfit(e.target.value)} autoFocus data-testid="fix-profit-input" />
            {newCost !== null && newCost >= 0 && <div className="mt-1 text-xs text-gray-500" data-testid="fix-profit-new-cost">New cost: {fmt(newCost)} excl VAT per unit</div>}
          </div>
          <div>
            <Label className="text-sm">Your password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="fix-profit-password-input" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="fix-profit-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="fix-profit-save-btn">{saving ? "Saving..." : "Update Profit"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
